import { Form } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  GroupItem,
  Label,
  SimpleItem,
} from 'devextreme-react/form';
import { RequiredRule } from 'devextreme-react/validator';
import React, { useEffect, useRef, useState } from 'react';
import '../../assets/bulk-modal.scss';
import { useTranslation } from '../../contexts/translation';
import { DataGridActionType } from '../../types/dataGridActionTypes';
import { BaseValueDataType } from '../../types/parameter.types';
import { dateFieldOptions } from '../../utils/dates.types';
import { validationNullableDateRule } from '../../utils/dateValidation';
import './EditCreateModal.scss';
import './TasksBulkEditModal.scss';

type Props = {
  data: any;
  isVisible: boolean;
  mutate?: () => void;
  onClose: () => void;
  onSubmit: (data: any, actionType: DataGridActionType) => void;
  actionType: DataGridActionType;
  transFormName: string;
  parameters: any;
  parameterChanged: any;
  units: any;
  selectedUnitId?: number;
};

/**
 * Modalová komponent pro vytváření/editaci záznamů typu Asset parameter measured v datagridu
 */
export default function AssetParameterMeasuredModal({
  isVisible,
  data,
  actionType,
  onClose,
  onSubmit,
  parameters,
  parameterChanged,
  units,
  selectedUnitId,
  transFormName,
}: Props) {
  const { translate } = useTranslation();
  const formRef = useRef<any>();
  const inputRef = useRef(null);
  const [selectedUnit, setSelectedUnit] = useState<string>();
  const [formData, setFormData] = useState<any>({
    ...data,
    ...(actionType === DataGridActionType.Insert && { visible: true }),
  });

  useEffect(() => {
    setFormData({
      ...data,
      ...(actionType === DataGridActionType.Insert && { visible: true }),
    });
  }, []);

  useEffect(() => {
    if (!data) return;
    setUnitType(data.unitId);
  }, [data]);

  useEffect(() => {
    if (!selectedUnitId) return;
    setUnitType(selectedUnitId);
    setFormData({ ...formData, unitId: selectedUnitId });
  }, [selectedUnitId]);

  const setUnitType = (selectedId: number) => {
    let tmpUnit = units.find((u: any) => u.id === selectedId);
    if (tmpUnit) setSelectedUnit(tmpUnit.baseValueDataType);
  };

  // Focus na inout
  const focusInput = (e: any) => {
    if (!parameters || parameters.length === 0) return;

    if (!data) {
      e.component.updateData('assetParameterId', parameters[0].id);
      e.component.updateData('measureToDate', new Date());
    }

    if (!selectedUnit) return <></>;

    setTimeout(() => {
      switch (selectedUnit) {
        case BaseValueDataType.Text:
          e.component.getEditor('valueText')?.focus();
          break;
        case BaseValueDataType.DateTime:
          e.component.getEditor('valueDate')?.focus();
          break;
        case BaseValueDataType.Date:
          e.component.getEditor('valueDate')?.focus();
          break;
        case BaseValueDataType.Float:
          e.component.updateData('valueFloat', null);
          e.component.getEditor('valueFloat')?.focus();
          break;
        case BaseValueDataType.Int:
          if (!data) e.component.updateData('valueInt', null);
          e.component.getEditor('valueInt').focus();
          break;
      }
    }, 500);
  };

  const getInput = () => {
    if (!selectedUnit) return <></>;

    switch (selectedUnit) {
      case BaseValueDataType.Text:
        return (
          <SimpleItem
            dataField="valueText"
            editorType="dxTextBox"
            editorOptions={{
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
          </SimpleItem>
        );
      case BaseValueDataType.DateTime:
        return (
          <SimpleItem
            dataField="valueDate"
            editorType="dxDateBox"
            editorOptions={{
              ...dateFieldOptions,
              ref: inputRef,
            }}
          >
            <CustomRule
              validationCallback={validationNullableDateRule}
              type={'custom'}
              message={'Somethind bad with time'}
              ignoreEmptyValue
            />
            <Label text={translate!('Value', transFormName)} />
          </SimpleItem>
        );
      case BaseValueDataType.Date:
        return (
          <SimpleItem
            dataField="valueDate"
            editorType="dxDateBox"
            editorOptions={{
              ...dateFieldOptions,
              ref: inputRef,
            }}
          >
            <CustomRule
              validationCallback={validationNullableDateRule}
              type={'custom'}
              message={'Somethind bad with time'}
              ignoreEmptyValue
            />
            <Label text={translate!('Value', transFormName)} />
          </SimpleItem>
        );
      case BaseValueDataType.Float:
        return (
          <SimpleItem
            dataField="valueFloat"
            editorType="dxNumberBox"
            editorOptions={{
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
          </SimpleItem>
        );
      case BaseValueDataType.Bool:
        return (
          <SimpleItem
            dataField="valueBool"
            editorType="dxSelectBox"
            editorOptions={{
              items: [
                { id: true, name: translate!('True', transFormName) },
                { id: false, name: translate!('False', transFormName) },
              ],
              valueExpr: 'id',
              displayExpr: 'name',
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
          </SimpleItem>
        );
      case BaseValueDataType.Int:
        return (
          <SimpleItem
            dataField="valueInt"
            editorType="dxNumberBox"
            editorOptions={{
              ref: inputRef,
            }}
          >
            <Label text={translate!('Value', transFormName)} />
          </SimpleItem>
        );
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(formData, actionType);
      }}
    >
      <Form
        formData={formData}
        showColonAfterLabel={true}
        showValidationSummary={true}
        ref={formRef}
        onContentReady={focusInput}
      >
        <GroupItem>
          <SimpleItem
            dataField="assetParameterId"
            editorType="dxSelectBox"
            editorOptions={{
              readOnly: actionType !== DataGridActionType.Insert,
              items: parameters,
              valueExpr: 'id',
              displayExpr: 'parameterName',
              onValueChanged: (val) => parameterChanged(val),
            }}
          >
            <Label text={translate!('Parameter', transFormName)} />
            <RequiredRule message={translate!('Parameter is required', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem>
          <SimpleItem
            dataField="unitId"
            editorType="dxSelectBox"
            editorOptions={{
              readOnly: actionType !== DataGridActionType.Insert,
              items: units,
              valueExpr: 'id',
              displayExpr: 'name',
            }}
          >
            <Label text={translate!('Unit', transFormName)} />
          </SimpleItem>
        </GroupItem>
        {/* Value options */}
        <GroupItem>{getInput()}</GroupItem>
        <GroupItem>
          <SimpleItem
            dataField="measureToDate"
            editorType="dxDateBox"
            editorOptions={{
              ...dateFieldOptions,
              // ...deliveredWhenProps,
              max: new Date(),
            }}
          >
            <CustomRule
              validationCallback={validationNullableDateRule}
              type={'custom'}
              message={'Somethind bad with time'}
              ignoreEmptyValue
            />
            <Label text={translate!('Measure to date', transFormName)} />
            <RequiredRule message={translate!('Measure to date is required', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem>
          <SimpleItem dataField="visible" editorType="dxCheckBox">
            <Label text={translate!('Visible', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem colCount={4}>
          <GroupItem colSpan={2}></GroupItem>
          <ButtonItem>
            <ButtonOptions type={'success'} text={translate!('Save', '')} useSubmitBehavior />
          </ButtonItem>
          <ButtonItem>
            <ButtonOptions type={'normal'} text={translate!('Close', '')} onClick={onClose} />
          </ButtonItem>
        </GroupItem>
      </Form>
    </form>
  );
}
